import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { RespiratoryReviewContext } from "./RespiratoryTherapistReviewProvider";
import {
  getLatestRespiratoryReviewAPI,
  getRespiratoryInspectionAPI,
  getRespiratoryReviewAPI,
  respiratoryReviewAPI,
  updateRespiratoryReviewAPI,
} from "../../../../services/respiratory.services";
import {
  Button,
  Col,
  Drawer,
  Form,
  Image,
  message,
  Modal,
  Row,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { LeftOutlined } from "@ant-design/icons";
import RespiratoryPatientAssessment from "../../InspectionForms/PatientAssessment/RespiratoryPatientAssessment";
import RespiratoryTherapistTreatmentPlan from "../../InspectionForms/TreatmentPlan/RespiratoryTherapistTreatmentPlan";
import bedImg from "../../../../assets/images/patientIcons/bed.svg";
import useIsMobile from "../../../../hooks/useIsMobile";
import "../../ipforms.css";
import "../../Doctor/InitialInspection/InitialInspectionHeader.css";
import "../../Doctor/DailyReview/DailyReview.css";

const RespiratoryTherapistReviewForm = ({
  editId,
  visible,
  setVisible,
  refreshPage,
  iAssessment,
  addMode,
  setAddMode,
  setActiveSegment,
  reviewData,
  setDailyReviewRefresh,
  dailyRefresh,
  segmentData,
  patientData,
}) => {
  const isMobile = useIsMobile();
  const formBodyRef = useRef(null);
  const location = useLocation();
  const params = Object.fromEntries(new URLSearchParams(location?.search));
  const [patientForm] = Form.useForm();
  const [treatmentForm] = Form.useForm();
  const { formData, setFormData } = useContext(RespiratoryReviewContext);
  const [activeTab, setActiveTab] = useState("1");
  const [isDirty, setIsDirty] = useState(false);
  const newReview = reviewData?.length === 0;
  const reviewId = reviewData?.[0]?.id;
  const newReviewId = addMode === false && reviewId ? reviewId : null;
  const reviewDate =
    segmentData?.find(
      (option) => option.value === editId && option.value !== "others"
    )?.label ||
    new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit" });

  const tabItems = useMemo(
    () => [
      {
        key: "1",
        label: isMobile ? "Patient" : "Patient Assessment",
      },
      {
        key: "2",
        label: isMobile ? "Treatment" : "Treatment Plan",
      },
    ],
    [isMobile]
  );

  useEffect(() => {
    if (formBodyRef.current) {
      formBodyRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  const handleSubmit = async (values, flag) => {
    try {
      if (editId || newReviewId) {
        await updateRespiratoryReviewAPI(editId || newReviewId, values);
        setDailyReviewRefresh();
      } else {
        await respiratoryReviewAPI(values);
        refreshPage();
        setAddMode(false);
        setDailyReviewRefresh();
      }

      message.success(`${flag} submitted successfully`);

      setIsDirty(false);
    } catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const onPatientSubmit = (values) => {
    handleSubmit(
      {
        ...formData,
        patient_assessment: values,
        admission_id: params?.patient,
      },
      "Patient Assessment"
    );
  };

  const onTreatmentSubmit = (values) => {
    handleSubmit(
      {
        ...formData,
        treatment_plan: values,
        admission_id: params?.patient,
      },
      "Treatment Plan"
    );
  };

  const openEdit = async () => {
    if (editId && newReview === false) {
      try {
        const response = await getRespiratoryReviewAPI(editId);
        setFormData(response.data.data);
      } catch (error) {
        console.log(`Error: ${error}`);
      }
    } else if (newReview === false) {
      try {
        const response = await getLatestRespiratoryReviewAPI(params?.patient);
        setFormData(response.data.data);
      } catch (error) {
        console.log(`Error: ${error}`);
      }
    } else {
      try {
        const response = await getRespiratoryInspectionAPI(iAssessment);
        setFormData(response.data.data);
      } catch (error) {
        console.log(`Error:${error}`);
      }
    }
  };

  useEffect(() => {
    openEdit();
  }, [editId, dailyRefresh]);

  const getFormKey = () => {
    switch (activeTab) {
      case "1":
        return patientForm.submit();
      case "2":
        return treatmentForm.submit();
      default:
        return "";
    }
  };

  const handleTabChange = (key) => {
    if (isDirty) {
      switch (activeTab) {
        case "1":
          patientForm.validateFields().then((values) => {
            setIsDirty(false);
            onPatientSubmit(values);
            setActiveTab(key);
          });
          break;
        case "2":
          treatmentForm.validateFields().then((values) => {
            setIsDirty(false);
            onTreatmentSubmit(values);
            setActiveTab(key);
          });
          break;
        default:
          setActiveTab(key);
      }
    } else {
      setActiveTab(key);
    }
  };

  const onFormValuesChange = () => {
    setIsDirty(true);
  };

  const handleCloseConfirm = () => {
    Modal.confirm({
      title: "Confirmation",
      content: "Are you sure you want to close?",
      onOk: () => {
        setVisible(false);
        setActiveSegment(reviewData[0]?.id);
      },
    });
  };

  const patientName = `${patientData?.admission?.patient?.first_name || ""} ${
    patientData?.admission?.patient?.last_name || ""
  }`.trim();

  return (
    <div>
      <Drawer
        styles={{
          body: {
            overflow: "hidden",
            paddingTop: 0,
            paddingBottom: 0,
            paddingInline: 24,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
        }}
        open={visible}
        width="100%"
        title={
          <div className="ia-drawer-header">
            <div className="ia-drawer-header__main">
              <LeftOutlined
                className="ia-drawer-header__back"
                onClick={handleCloseConfirm}
              />
              <div className="ia-drawer-header__identity">
                <div className="ia-drawer-header__name-row">
                  <Typography.Text
                    className="ia-drawer-header__name"
                    ellipsis={{ tooltip: patientName }}
                  >
                    {patientName}
                  </Typography.Text>
                  {patientData?.admission?.bed?.bed_no && (
                    <Tag color="orange" className="ia-drawer-header__bed">
                      <Image src={bedImg} preview={false} width={12} />{" "}
                      {patientData?.admission?.bed?.bed_no}
                    </Tag>
                  )}
                </div>
                <div className="ia-drawer-header__meta">
                  <Tag className="ia-drawer-header__tag">
                    {patientData?.admission?.patient?.gender}{" "}
                    {patientData?.admission?.patient?.age}
                  </Tag>
                  <Tag className="ia-drawer-header__tag">
                    IP ID: {patientData?.admission?.ip_id}
                  </Tag>
                  {reviewDate && (
                    <Tag className="ia-drawer-header__tag">{reviewDate}</Tag>
                  )}
                </div>
              </div>
            </div>
          </div>
        }
        footer={null}
        onClose={() => {
          setVisible(false);
          setActiveSegment(reviewData[0]?.id);
        }}
        closeIcon={null}
        keyboard={false}
      >
        <div className="daily-review-form-shell">
          <div className="ia-form-tabs daily-review-form-tabs">
            <Row>
              <Col
                xs={24}
                sm={24}
                md={{ span: 23, offset: 1 }}
                lg={{ span: 21, offset: 3 }}
                xl={{ span: 19, offset: 5 }}
                xxl={{ span: 18, offset: 6 }}
              >
                <Tabs
                  activeKey={activeTab}
                  items={tabItems}
                  onChange={handleTabChange}
                />
              </Col>
            </Row>
          </div>

          <div className="ia-form-panel">
            <div className="ia-form-body" ref={formBodyRef}>
              {activeTab === "1" && (
                <Form
                  id="review-patient-assessment"
                  form={patientForm}
                  layout="vertical"
                  onFinish={onPatientSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData}
                  className="ip-forms ip-forms-review"
                >
                  <RespiratoryPatientAssessment
                    review={true}
                    form={patientForm}
                    context={RespiratoryReviewContext}
                  />
                </Form>
              )}
              {activeTab === "2" && (
                <Form
                  id="review-treatment-plan"
                  form={treatmentForm}
                  layout="vertical"
                  onFinish={onTreatmentSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData}
                  className="ip-forms ip-forms-review"
                >
                  <RespiratoryTherapistTreatmentPlan
                    form={treatmentForm}
                    context={RespiratoryReviewContext}
                  />
                </Form>
              )}
            </div>

            <div className="ia-form-footer">
              {activeTab !== "2" && (
                <Button
                  onClick={() => {
                    getFormKey();
                    setVisible(false);
                  }}
                  style={{ marginRight: 8 }}
                >
                  Save & Close
                </Button>
              )}
              <Button
                onClick={() => {
                  getFormKey();
                  document.querySelector(".ip-forms")?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                  const nextTab = (parseInt(activeTab) + 1).toString();
                  if (activeTab === "2") {
                    setVisible(false);
                  } else {
                    setActiveTab(nextTab);
                  }
                }}
                type="primary"
              >
                {activeTab === "2" ? "Done" : " Save & Proceed"}
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default RespiratoryTherapistReviewForm;
